import { Button } from '@rocco/components/button';
import type { TeamMember, TeamMemberDetail } from './types';
import { useMemo } from 'react';
import { formatCategoryName } from './utils';
import { normalizeProjectTeamCategory } from '~/modules/project/util/project-team-category';
import { useTracker } from '../../../../../../tracker/src/react-bindings';

/**
 * Side navigation component for selecting different professional categories in the project team
 */

interface ProjectTeamSideNavProps {
    teamList: string[];
    activeTab: string;
    setActiveTab: (tab: string) => void;
    setCurrentIndex: (index: number) => void;
    teamData: TeamMember[];
    currentTeamMember: TeamMemberDetail;
}

export const ProjectTeamSideNav = ({
    teamList,
    activeTab,
    setActiveTab,
    setCurrentIndex,
    teamData,
}: ProjectTeamSideNavProps) => {
    const tracker = useTracker();
    // Display only categories that definitely exist
    const availableTeams = useMemo(() => {
        // If a pre-filtered list is provided
        if (teamList && teamList.length > 0) {
            return teamList;
        }

        // Filter categories directly
        const categoriesWithMembers = teamData
            .filter((category) => {
                // Select only categories with public members
                return (
                    category.TeamMembers &&
                    category.TeamMembers.some((member) => {
                        return member.IsPublic !== false;
                    })
                );
            })
            .map((category) => category.Category);

        return categoriesWithMembers;
    }, [teamList, teamData]);

    // Don't render if category list is empty
    if (!availableTeams || availableTeams.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-row gap-3">
            {availableTeams.map((team) => (
                <Button
                    key={team}
                    onClick={() => {
                        setActiveTab(team);
                        setCurrentIndex(0);
                        tracker.log('ProjectTeamClick', {
                            url: new URL(window.location.href),
                            data: {
                                Category: team,
                            },
                        });
                    }}
                    color={
                        normalizeProjectTeamCategory(team) ===
                        normalizeProjectTeamCategory(activeTab)
                            ? 'primary'
                            : 'secondary'
                    }
                >
                    {formatCategoryName(team)}
                </Button>
            ))}
        </div>
    );
};
